<template>
  <div class="apphome">
    <!-- 1.左栏 -->
    <div v-loading="isLoading" class="apphome-left">
      <!-- 1.1已开通应用 -->
      <div class="apphome-left_header">
        <h2>{{ $t('我的应用') }}</h2>
        <ul v-if="openedAppList.length" class="applist">
          <li
            v-for="(item, index) in openedAppList"
            :key="`${index}-${item.code}`"
            class="appitem open"
            @click="() => onAppClick(item)"
          >
            <img :src="item.img" />
            <div class="appitem-text">
              <h2>{{ item.appName }}</h2>
              <div :class="{ 'appitem-text--small': !isZh }"><span v-for="tip in item.description" :key="`${item.code}-${tip}`">{{ tip }}</span></div>
            </div>
          </li>
        </ul>

        <el-empty
          v-else
          class="home-empty"
          :image-size="185"
          :image="require('@/assets/404_images/empty.png')"
        >
          <div slot="description" class="el-empty__description">
            <span>{{ $t('暂无应用开通') }}</span>
            <!-- 暂不支持 -->
            <!-- <el-button type="text" style="font-size: 14px;" @click="goApply">{{ $t('去开通') }}</el-button> -->
          </div>
        </el-empty>
      </div>

      <!-- 1.2未开通应用 -->
      <div class="apphome-left_main">
        <h2>{{ $t('未开通应用') }}</h2>
        <ul v-if="unopenedAppList.length" class="applist">
          <li
            v-for="(item, index) in unopenedAppList"
            :key="`${index}-${item.code}`"
            class="appitem open"
            @click="() => onAppClick(item)"
          >
            <img :src="item.img" />
            <div class="appitem-text">
              <h2>{{ item.appName }}</h2>
              <div :class="{ 'appitem-text--small': !isZh }"><span v-for="tip in item.description" :key="`${item.code}-${tip}`">{{ tip }}</span></div>
            </div>
          </li>
        </ul>

        <el-empty
          v-else
          class="home-empty"
          :image-size="185"
          :image="require('@/assets/404_images/empty.png')"
          :description="$t('暂无未开通应用')"
        />
      </div>
    </div>

    <!-- 2.右栏 -->
    <div class="apphome-right">
      <!-- 2.1banner -->
      <div class="apphome-right_banner" :class="{ 'apphome-right_banner--small': !isZh }">
        <div class="banner-title">{{ $t('技研智联-专注纺织行业数字化解决方案') }}</div>
        <div class="banner-tips">{{ $t('通过数字化整体方案，解决纺织印染企业在生产、工艺、设备、能源等环节的痛点。') }}</div>
      </div>

      <!-- 2.2消息通知 -->
      <div class="apphome-right_main">
        <div class="apphome-right_main--top">
          <h2>{{ $t('消息通知') }}</h2>
          <div v-if="msgCount" class="message-count">{{ msgCount }}</div>
          <el-button type="text" class="message-more" @click="goMsgCenter">{{ $t('更多') }}</el-button>
        </div>

        <el-scrollbar v-if="msgList.length" class="apphome-right_main--message">
          <div v-for="(msg, index) in msgList" :key="`msg-${index}`" class="msg-item" :class="{'un-read': !msg.isRead}">
            <div class="msg-item_title">
              <el-tooltip
                ref="msgtitleTooltip"
                :content="msg.title"
                placement="top"
                :disabled="!msgOverflow.includes(msg.title)"
              >
                <span class="msg-item_title--text">{{ msg.title }}</span>
              </el-tooltip>
              <span class="msg-item_title--time">{{ msg.receiveTime }}</span>
            </div>

            <el-tooltip
              ref="msg-tooltip"
              :content="msg.content"
              placement="left"
              :hide-after="30000"
            >
              <div class="msg-item_content">{{ msg.content }}</div>
            </el-tooltip>
          </div>
        </el-scrollbar>

        <el-empty
          v-else
          class="home-empty"
          :image-size="185"
          :image="require('@/assets/404_images/empty.png')"
          :description="$t('暂无消息')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { baseApplicationApi } from '@klinx/base/src/api/index.js';
import { axiosAllSettled } from '@klinx/utils';
import appRouterMixin from '@/layout/mixin/get-app-router';
import { APP_CONFIG_MAP, EXCLUDE_APP } from './config';
import db from '@klinx/utils/localstorage';
import { mapGetters } from 'vuex';

export default {
  name: 'Home',
  mixins: [appRouterMixin],
  data() {
    return {
      isLoading: true,
      isZh: ['zh', 'tw'].includes(db.get('LANGUAGE', 'zh')),
      lastPageKey: '', // 上一页历史state的key
      lastApp: {}, // 上一次选择的应用
      appVersion: '',
      appList: [], // 应用列表
      msgOverflow: [] // 消息
    };
  },
  computed: {
    openedAppList() {
      return this.appList.filter(item => item.isOpen);
    },
    unopenedAppList() {
      return this.appList.filter(item => !item.isOpen);
    },
    msgData() {
      return this.$store.state.account.msgData;
    },
    msgCount() {
      const count = this.msgData.unReadCount || '';
      return count ? (count <= 99 ? count : '99+') : '';
    },
    msgList() {
      return this.msgData.msgList || [];
    },
    ...mapGetters([
      'envConfig'
    ])
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 记录进来首页时的历史state页面key
      vm.lastPageKey = window.history.state?.key;
    });
  },
  beforeRouteLeave(to, from, next) {
    // 返回的时候重置原应用（返回上一页时key不一样，前进时key一样）
    if (this.lastPageKey !== window.history.state?.key && this.lastApp.code !== 'home') {
      this.$store.commit('account/SET_CURRENT_APP', this.lastApp);
      this.$store.commit('account/setRoutes', this.lastApp.routes);
    }
    next();
  },
  async mounted() {
    this.resetApp();
    try {
      this.appVersion = await this.getVersion();
      await this.queryAppList();
    } catch (e) {
      console.log(this.$t('查询应用列表出错'), e);
    } finally {
      this.$nextTick(() => {
        this.isLoading = false;
      });
    }
  },
  methods: {
    axiosAllSettled,
    resetApp() {
      // 缓存原来已选的应用
      this.lastApp = {
        ...this.$store.state.account.currentApp,
        routes: this.$store.state.account.routes
      };

      // 重置路由
      this.$store.commit('account/SET_CURRENT_APP', {
        code: 'home',
        appName: this.$t('ITEX 企业工作台')
      });
      document.title !== this.$t('技研工业云平台3.0') && (document.title = this.$t('技研工业云平台3.0'));
      this.$store.commit('account/removeRoutes');
    },
    /**
     * 查询应用全集及开通情况
     */
    async queryAppList() {
      try {
        const [allAppRes, myAppRes] = await this.axiosAllSettled([
          baseApplicationApi.getAllApp,
          baseApplicationApi.getMyAppCurTenant
        ]);

        if (allAppRes.isSuccess && allAppRes.data?.length && myAppRes.isSuccess) {
          const allApp = allAppRes.data;
          const myApp = myAppRes.data || [];
          allApp.forEach(item => {
            // 剔除不需要展示或者禁用的
            if (EXCLUDE_APP.includes(item.code.toLowerCase()) || !APP_CONFIG_MAP[item.code] || APP_CONFIG_MAP[item.code]?.disabled) {
              return;
            }

            // 记录开通状态
            const isOpen = myApp.some(app => app.code === item.code && !app.hidden); // 是否已开通、是否不隐藏
            const description = this.isZh ? APP_CONFIG_MAP[item.code].description || '' : APP_CONFIG_MAP[item.code].enDesc;
            this.appList.push({
              ...item,
              isOpen,
              version: this.appVersion || APP_CONFIG_MAP[item.code].version,
              description: description.split('|'),
              img: require(`@/assets/home/${item.code}-icon.png`)
            });
          });
          this.$store.commit('account/setAppList', this.appList);
          return Promise.resolve('');
        }
        return Promise.reject('');
      } catch (e) {
        console.log(e);
        return Promise.reject(e);
      }
    },
    updateTooltips() {
      if (!this.$refs.msgtitleTooltip) {
        return;
      }
      this.$refs.msgtitleTooltip.forEach(tool => {
        const el = tool.$el;
        if (!this.msgOverflow.includes(el.innerText) && el.scrollWidth > el.offsetWidth) {
          this.msgOverflow.push(el.innerText);
        }
      });
    },
    goMsgCenter() {
      this.$router.push({
        path: '/base-config/message/center'
      });
    }
    // goApply() {}
  }
};
</script>

<style lang="scss" scoped>
$right-width: 450px;
h2 {
  color: $text;
  font-weight: 600;
}

.apphome {
  width: fit-content;
  height: 100%;
  overflow-x: hidden;
  min-height: 100%;
  display: flex;
  background: #F7F8FA;
  padding: $spacing-l-24 $spacing-l-24;
  padding-right: $spacing-m-16;
  &-left {
    flex: 1;
    margin-right: $spacing-l-24;
    width: calc(100vw - #{$right-width} - #{$spacing-l-24} * 3);
    min-width: 986px;
    display: flex;
    flex-direction: column;
    &_header, &_main {
      display: flex;
      flex-direction: column;
    }

    &_main {
      flex: 1;
      margin-top: $spacing-xl-32;
    }

    .applist {
      display: flex;
      flex-wrap: wrap;
      margin-bottom: $spacing-xl-32;
      .appitem {
        list-style: none;
        background: $fill-bg;
        margin-top: $spacing-l-24;
        border-radius: $border-radius-8;
        width: calc((100% - #{$spacing-l-24} * 2) / 3);
        display: flex;
        align-items: center;
        box-shadow: 0px 4px 10px 0px rgba(58, 113, 255, 0.09);
        cursor: pointer;
        min-width: 318px;
        min-height: 96px;
        max-height: 145px;
        overflow: hidden;
        &:not(:nth-of-type(3n)) {
          margin-right: $spacing-l-24;
        }

        &:hover {
          box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.13),0px 4px 10px 0px rgba(58, 113, 255, 0.09);
        }

        &.open {
          padding: $spacing-l-24 $spacing-l-24;
        }

        &.unopen{
          position: relative;
          background: $fill-bg;
          img {
            max-width: 453px;
            max-height: 100%;
            border-radius: $border-radius-8;
          }

          .appitem-text {
            position: absolute;
            height: 100%;
            padding-right: 24px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
        }

        .appitem-text {
          h2 {
            margin-bottom: $spacing-s-8;
          }

          span {
            color: $text-vice;
            &:not(:last-child):after {
              content: '';
              width: 1px;
              height: 9px;
              background: #D8D8D8;
              margin: 0px $spacing-s-8;
              display: inline-block;
            }
          }
        }
      }
    }
  }

  &-right {
    width: $right-width;
    display: flex;
    flex-direction: column;
    &_banner {
      background: url('../../assets/home/banner.png');
      background-size: 578px 235px;
      background-position: 40%;
      width: 100%;
      height: 235px;
      padding: $line-height-xxxl-58 0px 0px $line-height-xl-32;
      border-radius: 8px;
      .banner-title {
        width: 300px;
        line-height: $line-height-xl-32;
        font-size: $font-size-xl-24;
        margin-bottom: $spacing-l-24;
        font-weight: 600;
      }

      .banner-tips {
        width: 255px;
        color: $text-vice;
        font-size: $font-size-sm-13;
        line-height: 20px;
        text-align: justify;
      }

      &--small {
        padding: $line-height-xxl-44 0px 0px $line-height-xl-32;
        .banner-title {
          width: 320px;
        }

        .banner-tips {
          width: 270px;
          text-align: left;
          font-size: $font-size-sm-13 + 1px;
        }
      }
    }

    &_main {
      display: flex;
      flex-direction: column;
      height: calc(100% - 235px);
      &--top {
        width: 100%;
        display: flex;
        align-items: center;
        margin-top: $spacing-xl-32;
        position: relative;
        .message-count {
          background: $main-hover-light;
          border-radius: 4px;
          color: $main;
          padding: 2px 10px;
          height: 22px;
          font-size: $font-size-ss-12;
          line-height: $line-height-s-18;
          margin-left: $spacing-m-16;
        }

        .message-more {
          position: absolute;
          right: 0px;
          margin-right: 0px;
        }
      }

      &--message {
        flex: 1;
        background: #fff;
        border-radius: 8px;
        padding: $spacing-m-16 $spacing-m-16;
        margin-top: $spacing-m-16;
        overflow: hidden;
        text-overflow: ellipsis;
        .msg-item {
          border-bottom: 1px solid #F7F8FA;
          padding: 0px 0px 8px 16px;
          position: relative;
          max-height: 75px;
          overflow: hidden;
          &:not(:first-child) {
            margin-top: 12px;
          }

          &_title {
            line-height: $line-height-m-22;
            display: flex;
            align-items: center;
            position: relative;
            justify-content: space-between;
            &--text {
              overflow: hidden;
              margin-right: 8px;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            &--time {
              flex-shrink: 0;
              color: $text-sub;
              font-size: $font-size-ss-12;
            }
          }

          &_content {
            color: $text-sub;
            line-height: $line-height-m-22;
            word-break: break-all;
            max-height: calc(#{$line-height-m-22} * 2);
            text-overflow: ellipsis;
            letter-spacing: 0;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;  /*要显示的行数*/
            -webkit-box-orient: vertical;
          }

          &.un-read:before {
            content: '';
            background: $main-hover-light;
            display: block;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            position: absolute;
            top: 5px;
            left: 0px;
          }

          &.un-read .msg-item_title:before {
            content: '';
            background: $main;
            display: block;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            position: absolute;
            left: -13px;
          }
        }

        ::v-deep .el-scrollbar__wrap {
          margin-right: -24px !important;
          overflow-x: auto;
          padding-right: 15px;
        }
      }
    }
  }

  .home-empty {
    flex: 1;
    border-radius: 8px;
    justify-content: flex-start;
    margin-top: $spacing-m-16;
    background: $fill-bg;
    box-shadow: 0px 4px 10px 0px rgba(58, 113, 255, 0.09);
  }
}

@media screen and (min-width: 1920px) {
  .appitem {
    height: 144px;
  }

  h2 {
    font-size: $font-size-xl-24;
    line-height: $line-height-xl-32;
  }

  .appitem-text {
    font-size: $font-size-ml-16;
    line-height: $line-height-l-24;
    &--small {
      font-size: $font-size-ml-16 - 1px;
    }
  }

  .open {
    h2 {
      margin-bottom: $spacing-s-8;
    }

    img {
      width: 96px;
      height: 96px;
      margin-right: $spacing-m-16;
    }
  }

  .unopen {
    .appitem-text {
      left: 150px;
    }
  }

  ::v-deep .message-more {
    font-size: $font-size-ll-20;
  }
}

@media screen and (max-width: 1919px) {
  .appitem {
    height: 128px;
    width: calc((100% - #{$spacing-m-16} * 2) / 3) !important;
    &:not(:nth-of-type(3n)) {
      margin-right: $spacing-m-16 !important;
    }
  }

  h2 {
    font-size: $font-size-ll-20;
    line-height: 28px;
  }

  .appitem-text {
    font-size: $font-size-ss-12;
    line-height: $line-height-s-18;
    &--small {
      font-size: $font-size-ss-12 - 1px;
    }
  }

  .open img {
    width: 80px;
    height: 80px;
    margin-right: $spacing-m-16;
  }

  .unopen {
    .appitem-text {
      left: 130px;
    }
  }

  ::v-deep .message-more {
    font-size: $font-size-mm-14;
  }
}

@media screen and (max-width: 1599px) {
  .appitem {
    height: 120px;
  }

  h2 {
    font-size: $font-size-ml-16;
    line-height: $line-height-l-24;
  }

  .appitem-text {
    font-size: $font-size-ss-12;
    line-height: $line-height-s-18;
    &--small {
      font-size: $font-size-ss-12 - 1px;
    }
  }

  .open img {
    width: 72px;
    height: 72px;
    margin-right: $spacing-m-16;
  }
}

@media screen and (max-width: 1439px) {
  .appitem {
    height: 96px;
  }

  .open {
    h2 {
      margin-bottom: 4px !important;
    }

    img {
      width: 48px;
      height: 48px;
      margin-right: 12px;
    }
  }

  .unopen {
    .appitem-text {
      left: 95px;
    }
  }
}

@media screen and (max-width: 1366px) {
  .apphome-right {
    width: 315px;
    &_banner {
      height: 200px;
      background-position: 65% 100%;
      padding: 24px 0 0 32px;
      .banner-title {
        line-height: 26px;
        font-size: 20px;
        margin-bottom: 12px;
        width: 250px;
      }

      .banner-tips {
        width: 156px;
        line-height: 20px;
        text-align: justify;
      }
    }
  }
}
</style>
